import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import {
  Mail,
  Sparkles,
  Shield,
  ArrowLeft,
  Send,
  Key,
} from "lucide-react";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(
        "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/send-reset",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(email),
        }
      );

      if (response.ok) {
        toast.success(
          "If this email exists in our system, a reset link has been sent.",
          {
            duration: 5000,
            style: {
              borderRadius: '12px',
              background: '#10b981',
              color: '#fff',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          }
        );
        setEmail("");
      } else {
        let errorMessage = "Failed to send reset link.";
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        toast.error(errorMessage, {
          duration: 4000,
          style: {
            borderRadius: '12px',
            background: '#ef4444',
            color: '#fff',
          },
        });
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Something went wrong. Please try again.", {
        duration: 4000,
        style: {
          borderRadius: '12px',
          background: '#ef4444',
          color: '#fff',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 relative overflow-y-auto"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-black/60" />

      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: '12px',
            background: '#fff',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            fontSize: '14px',
          },
        }}
      />

      <div className="relative z-10 w-full max-w-md mx-auto">
        {/* Decorative elements - hidden on mobile */}
        <div className="hidden sm:block absolute -top-20 -left-20 w-64 h-64 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="hidden sm:block absolute -bottom-20 -right-20 w-64 h-64 bg-gradient-to-r from-pink-400/20 to-purple-400/20 rounded-full blur-3xl" />

        {/* Back to Login Button */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 mb-4 text-white/80 hover:text-white transition-colors duration-200 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back to Login</span>
        </Link>

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200 p-5 sm:p-6 md:p-8 transform transition-all duration-300 hover:shadow-3xl">
          {/* Brand */}
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-lg opacity-50 animate-pulse" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-lg transform transition-transform hover:scale-105 duration-300">
                <Key className="w-7 h-7 sm:w-8 sm:h-8 text-white animate-bounce" />
              </div>
            </div>
            <h1 className="mt-4 sm:mt-5 text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent text-center">
              Forget Password?
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 text-center px-4">
              Don't worry! We'll send you a reset link
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="student@msa.edu"
                  required
                  className="
                    w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm
                    border-2 border-gray-200 rounded-xl
                    text-gray-800 placeholder:text-gray-400
                    focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                  "
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Enter your registered email address and we'll send you a link to reset your password.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`
                w-full flex items-center justify-center gap-2
                px-4 py-2.5 sm:py-3 rounded-xl text-sm font-semibold
                transition-all duration-200 transform active:scale-95 sm:hover:scale-105
                ${loading
                  ? "bg-gradient-to-r from-blue-400 to-purple-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-md hover:shadow-lg"}
                text-white
              `}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin w-4 h-4" />
                  <span>Sending Reset Link...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Reset Link</span>
                </>
              )}
            </button>

            {/* Helpful Tips */}
            <div className="mt-4 sm:mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-semibold text-blue-900 mb-1">
                    What happens next?
                  </h4>
                  <p className="text-xs text-blue-700">
                    We'll send a password reset link to your email. Click the link in the email to create a new password.
                  </p>
                </div>
              </div>
            </div>

            {/* Login Link */}
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-600">
                Remembered your password?{" "}
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
                >
                  Back to Login
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Security Notice */}
        <div className="mt-4 sm:mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-white/80">
            <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Secure password reset with 256-bit encryption</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 sm:mt-6 text-center">
          <p className="text-xs text-white/60">
            © 2024 MSA Student Portal. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;